// Pure helpers for the Assets tab. User-visible text comes from the `edges.assets.*` / `edges.time.*` locale keys.
import i18n from "../../i18n";

export const FRESH_MS = 5 * 60 * 1000;

// latestRows: [{time, device_id, device_name, tag_name, unit, value}] (only tags with data in last 24h)
// -> Map(device_id -> Map(tag_name -> {time, value, unit}))
export function buildLatestMap(latestRows) {
  const map = new Map();
  (latestRows || []).forEach((r) => {
    if (!r?.device_id || !r?.tag_name) return;
    if (!map.has(r.device_id)) map.set(r.device_id, new Map());
    map.get(r.device_id).set(r.tag_name, { time: r.time, value: r.value, unit: r.unit });
  });
  return map;
}

// deviceLatest: Map(tag_name -> {time,value,unit}) | undefined -> {fresh, count}
// fresh: at least one tag's time is within FRESH_MS of now. count: number of tags with data.
export function deviceSummary(deviceLatest, nowMs = Date.now()) {
  if (!deviceLatest || deviceLatest.size === 0) return { fresh: false, count: 0 };
  let fresh = false;
  deviceLatest.forEach(({ time }) => {
    const t = time ? new Date(time).getTime() : NaN;
    if (!isNaN(t) && nowMs - t <= FRESH_MS) fresh = true;
  });
  return { fresh, count: deviceLatest.size };
}

// tags: [{id,name,label,unit}] (full discovered list) merged with deviceLatest (recent-data map)
// -> [{id,name,label,unit,value,time,status,statusKey}]; statusKey: "fresh" | "stale" | "none", status is its localized label
export function mergeDeviceTags(tags, deviceLatest, nowMs = Date.now()) {
  return (tags || []).map((tag) => {
    const hit = deviceLatest?.get(tag.name);
    if (!hit) {
      return { ...tag, value: null, time: null, statusKey: "none", status: i18n.t("edges.assets.statusNone") };
    }
    const t = hit.time ? new Date(hit.time).getTime() : NaN;
    const statusKey = !isNaN(t) && nowMs - t <= FRESH_MS ? "fresh" : "stale";
    const status = i18n.t(statusKey === "fresh" ? "edges.assets.statusFresh" : "edges.assets.statusStale");
    return { ...tag, value: hit.value, time: hit.time, unit: hit.unit ?? tag.unit, status, statusKey };
  });
}

// ISO time -> "X min ago" / "X h ago" / "X d ago" in the current language (null/invalid -> "—")
export function formatAgo(time, nowMs = Date.now()) {
  if (!time) return "—";
  const t = new Date(time).getTime();
  if (isNaN(t)) return "—";
  const diffMin = Math.max(0, Math.round((nowMs - t) / 60000));
  if (diffMin < 1) return i18n.t("edges.time.justNow");
  if (diffMin < 60) return i18n.t("edges.time.minutesAgo", { n: diffMin });
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return i18n.t("edges.time.hoursAgo", { n: diffHour });
  return i18n.t("edges.time.daysAgo", { n: Math.round(diffHour / 24) });
}
